// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Load Tickets CRUD
// The core operational page — tracks every load from pickup to disposal
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePolling } from '../../hooks/usePolling';
import { useClinDescriptions } from '../../hooks/useClinDescriptions';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Switch,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Divider,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconCamera, IconX, IconCurrentLocation, IconMapPin, IconPhoto, IconArrowBackUp,
  IconBuilding, IconId, IconDownload, IconCircleCheck, IconAlertCircle,
} from '@tabler/icons-react';
import { exportCSV } from '../../lib/exportCSV';
import { notifications } from '../../lib/notifications';

import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAllTickets, invalidateTicketCache } from '../../hooks/useAllTickets';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS, DEBRIS_TYPES, DEBRIS_TYPE_OPTIONS, TICKET_STATUS_LABELS } from '../../types/debris';
import type { DebrisLoadTicket, DebrisType, TicketStatus } from '../../types/debris';
import { fetchTable, TABLES, FIELDS, WORKER_URL } from '../../lib/api-switch';
import { queryRecords } from '../../api/client';

// ── Compact signature pad (restores from saved base64 on mount) ──
function SignaturePad({ label, value, onCapture }: { label: string; value?: string; onCapture: (dataUrl: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [signed, setSigned] = useState(false);
  const getCtx = () => canvasRef.current?.getContext('2d') || null;

  // Restore saved signature on mount
  useEffect(() => {
    if (value && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height);
          ctx.drawImage(img, 0, 0, canvasRef.current!.width, canvasRef.current!.height);
          setSigned(true);
        };
        img.src = value;
      }
    }
  }, []); // only on mount

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const ctx = getCtx(); if (!ctx) return;
    setDrawing(true);
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;
    ctx.beginPath(); ctx.moveTo(x, y);
  }, []);
  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) return; const ctx = getCtx(); if (!ctx) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;
    ctx.lineWidth = 2; ctx.strokeStyle = '#000'; ctx.lineCap = 'round'; ctx.lineTo(x, y); ctx.stroke();
  }, [drawing]);
  const endDraw = useCallback(() => {
    setDrawing(false);
    if (canvasRef.current) { onCapture(canvasRef.current.toDataURL('image/png')); setSigned(true); }
  }, [onCapture]);
  const clear = () => {
    const ctx = getCtx();
    if (ctx && canvasRef.current) { ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); setSigned(false); onCapture(''); }
  };
  return (
    <div>
      <Group justify="space-between" mb={4}>
        <Text size="xs" fw={600}>{label}</Text>
        {signed && <Badge size="xs" color="var(--color-success)">Signed</Badge>}
      </Group>
      <canvas ref={canvasRef} width={400} height={140}
        style={{ border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'crosshair', touchAction: 'none', background: '#fff', width: '100%', maxWidth: 400 }}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
      <Button size="compact-xs" variant="subtle" mt={2} onClick={clear}>Clear</Button>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
}

const STATUS_OPTIONS = Object.entries(TICKET_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }));

const STATUS_COLORS: Record<string, string> = {
  draft: '#939598', in_transit: 'var(--color-primary-700)', at_site: '#C68700', verified: 'var(--color-success)',
  delivered: '#03AC9E', disputed: 'var(--color-error)', voided: '#939598',
};

const DEBRIS_TYPE_SELECT = DEBRIS_TYPE_OPTIONS;

const EMPTY: Partial<DebrisLoadTicket> = {
  ticketNumber: '', truckId: '', truckNumber: '', crewId: '', contractorId: '',
  zoneId: '', disposalSiteId: '', debrisType: '', loadPercentage: '',
  calculatedCubicYards: '', pickupLat: '', pickupLng: '', pickupAddress: '',
  pickupTime: '', disposalTime: '', monitorName: '', status: 'in_transit', notes: '',
  payCodeId: '', paProjectId: '',
};

export default function LoadTickets() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const isAdmin = useIsAdmin();
  const { clinLabel } = useClinDescriptions();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    loadLoadTickets, createLoadTicket, updateLoadTicket, deleteLoadTicket,
  } = useDebrisOpsStore();
  const { tickets: allTickets, loading: loadingTickets } = useAllTickets();
  const tickets = allTickets as any[];

  const {
    contractors, crews, trucks, zones, disposalSites,
    loadContractors, loadCrews, loadTrucks, loadZones, loadDisposalSites,
  } = useDebrisStore();

  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [completeFilter, setCompleteFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisLoadTicket | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisLoadTicket | null>(null);
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [form, setForm] = useState<Partial<DebrisLoadTicket>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  // Photo state — only load photo at ticket level (before/after now per-stop)
  const [loadPhoto, setLoadPhoto] = useState<{ data: string; name: string } | null>(null);

  // View-only detail
  const [viewTicket, setViewTicket] = useState<DebrisLoadTicket | null>(null);

  // Photo lightbox
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxLabel, setLightboxLabel] = useState('');

  // Cert PDF viewer
  const [certPdfUrl, setCertPdfUrl] = useState<string | null>(null);
  const [certPdfTitle, setCertPdfTitle] = useState('');
  const [certPdfLoading, setCertPdfLoading] = useState<string | null>(null);

  const TRMETA_RE = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;

  const viewTruckCert = async (truckIdOrNumber: string, e?: React.MouseEvent) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    // Try by ID first, then by truck number
    let truck = trucks.find(t => String(t.id) === truckIdOrNumber);
    if (!truck) truck = trucks.find(t => t.truckNumber === truckIdOrNumber);
    if (!truck) {
      notifications.show({ message: `Truck not found: ${truckIdOrNumber}`, color: '#C68700' });
      return;
    }
    // Decode TRMETA to get documentId
    const raw = (truck as any).notes || '';
    const mm = raw.match(TRMETA_RE);
    let docId = '';
    if (mm) { try { const meta = JSON.parse(mm[1]); docId = meta.documentId || ''; } catch {} }
    if (!docId) {
      notifications.show({ message: `No cert PDF on file for truck #${truck.truckNumber}`, color: '#C68700' });
      return;
    }
    setCertPdfLoading(truckId);
    try {
      const { getV2FileDownloadUrl } = await import('../../lib/api-switch');
      const rawUrl = await getV2FileDownloadUrl(docId);
      const proxyUrl = `/api/filepreview?url=${encodeURIComponent(rawUrl)}`;
      setCertPdfUrl(proxyUrl);
      setCertPdfTitle(`Truck Cert — #${truck.truckNumber} (${truck.cubicYardCapacity || '?'} CY)`);
    } catch (err) {
      console.error('[LoadTickets] cert PDF error', err);
      notifications.show({ message: 'Failed to load cert PDF', color: 'var(--color-error)' });
    }
    setCertPdfLoading(null);
  };


  // Monitor options for loading monitor dropdown
  const [monitorOptions, setMonitorOptions] = useState<Array<{ value: string; label: string }>>([]);

  // PA cross-link options (loaded once on component mount)
  const [payCodeOptions, setPayCodeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [paProjectOptions, setPaProjectOptions] = useState<Array<{ value: string; label: string }>>([]);

  // Directory: linked disposal facility
  const [linkedFacilityId, setLinkedFacilityId] = useState<number | null>(null);
  const [linkedFacilityName, setLinkedFacilityName] = useState('');
  const [facilityQuery, setFacilityQuery] = useState('');
  const [facilityResults, setFacilityResults] = useState<Array<{ id: number; name: string; address: string; city: string }>>([]);
  const [facilitySearching, setFacilitySearching] = useState(false);
  const facilityDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Directory: linked monitor person
  const [linkedMonitorPersonId, setLinkedMonitorPersonId] = useState<number | null>(null);
  const [linkedMonitorPersonName, setLinkedMonitorPersonName] = useState('');
  const [monitorPersonQuery, setMonitorPersonQuery] = useState('');
  const [monitorPersonResults, setMonitorPersonResults] = useState<Array<{ id: number; name: string; title: string; email: string }>>([]);
  const [monitorPersonSearching, setMonitorPersonSearching] = useState(false);
  const monitorPersonDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Directory: linked contractor org
  const [linkedContractorOrgId, setLinkedContractorOrgId] = useState<number | null>(null);
  const [linkedContractorOrgName, setLinkedContractorOrgName] = useState('');
  const [contractorOrgQuery, setContractorOrgQuery] = useState('');
  const [contractorOrgResults, setContractorOrgResults] = useState<Array<{ id: number; name: string; city: string }>>([]);
  const [contractorOrgSearching, setContractorOrgSearching] = useState(false);
  const contractorOrgDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stop address autocomplete (shared state — one active at a time)
  const [stopAddrResults, setStopAddrResults] = useState<Array<{ label: string; lat: string; lng: string }>>([]);
  const [stopAddrActiveIdx, setStopAddrActiveIdx] = useState<number>(-1);
  const stopAddrTimer = useMemo(() => ({ current: null as ReturnType<typeof setTimeout> | null }), []);
  const fetchStopAddr = (q: string, stopIdx: number) => {
    setStopAddrActiveIdx(stopIdx);
    if (stopAddrTimer.current) clearTimeout(stopAddrTimer.current);
    if (q.length < 3) { setStopAddrResults([]); return; }
    stopAddrTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${WORKER_URL}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`, {
          headers: { 'Origin': 'https://apps.opengov.com' },
        });
        if (!r.ok) return;
        const data = await r.json();
        setStopAddrResults((data.items || []).filter((i: any) => i.address?.label || i.title).map((i: any) => ({
          label: i.address?.label || i.title,
          lat: i.position ? String(i.position.lat) : '',
          lng: i.position ? String(i.position.lng) : '',
        })));
      } catch { setStopAddrResults([]); }
    }, 250);
  };

  const handleFacilityQuery = (q: string) => {
    setFacilityQuery(q);
    if (facilityDebounce.current) clearTimeout(facilityDebounce.current);
    if (!q.trim()) { setFacilityResults([]); return; }
    facilityDebounce.current = setTimeout(async () => {
      if (!TABLES.facilities) return;
      setFacilitySearching(true);
      try {
        const FF = FIELDS.facilities;
        const res = await queryRecords(TABLES.facilities, {
          filters: FF.status ? [{ field: FF.status, operator: 'eq', value: 'active' }] : [],
          limit: 50,
        });
        const lower = q.toLowerCase();
        const hits = res.records
          .filter((r: any) => {
            const name = String(r[FF.name] ?? '').toLowerCase();
            const city = String(r[FF.city] ?? '').toLowerCase();
            const addr = String(r[FF.street] ?? '').toLowerCase();
            return name.includes(lower) || city.includes(lower) || addr.includes(lower);
          })
          .slice(0, 8)
          .map((r: any) => ({
            id: r.record_id as number,
            name: String(r[FF.name] ?? ''),
            address: String(r[FF.street] ?? ''),
            city: String(r[FF.city] ?? ''),
          }));
        setFacilityResults(hits);
      } catch { setFacilityResults([]); }
      finally { setFacilitySearching(false); }
    }, 300);
  };

  const handleMonitorPersonQuery = (q: string) => {
    setMonitorPersonQuery(q);
    if (monitorPersonDebounce.current) clearTimeout(monitorPersonDebounce.current);
    if (!q.trim()) { setMonitorPersonResults([]); return; }
    monitorPersonDebounce.current = setTimeout(async () => {
      if (!TABLES.people) return;
      setMonitorPersonSearching(true);
      try {
        const FP = FIELDS.people;
        const res = await queryRecords(TABLES.people, {
          filters: [],
          limit: 50,
        });
        const lower = q.toLowerCase();
        const hits = res.records
          .filter((r: any) => {
            const first = String(r[FP.firstName] ?? '').toLowerCase();
            const last = String(r[FP.lastName] ?? '').toLowerCase();
            const email = String(r[FP.email] ?? '').toLowerCase();
            return `${first} ${last}`.includes(lower) || email.includes(lower);
          })
          .slice(0, 8)
          .map((r: any) => {
            const first = String(r[FP.firstName] ?? '');
            const last = String(r[FP.lastName] ?? '');
            return {
              id: r.record_id as number,
              name: [first, last].filter(Boolean).join(' ') || `Person #${r.record_id}`,
              title: String(r[FP.title] ?? ''),
              email: String(r[FP.email] ?? ''),
            };
          });
        setMonitorPersonResults(hits);
      } catch { setMonitorPersonResults([]); }
      finally { setMonitorPersonSearching(false); }
    }, 300);
  };

  const handleContractorOrgQuery = (q: string) => {
    setContractorOrgQuery(q);
    if (contractorOrgDebounce.current) clearTimeout(contractorOrgDebounce.current);
    if (!q.trim()) { setContractorOrgResults([]); return; }
    contractorOrgDebounce.current = setTimeout(async () => {
      if (!TABLES.organizations) return;
      setContractorOrgSearching(true);
      try {
        const FO = FIELDS.organizations;
        const res = await queryRecords(TABLES.organizations, {
          filters: [{ field: FO.status, operator: 'eq', value: 'active' }],
          search: q.trim(),
          limit: 50,
        });
        setContractorOrgResults(res.records.slice(0, 8).map((r: any) => ({
          id: r.record_id as number,
          name: String(r[FO.name] ?? ''),
          city: String(r[FO.city] ?? ''),
        })));
      } catch { setContractorOrgResults([]); }
      finally { setContractorOrgSearching(false); }
    }, 300);
  };

  const handleLoadPhotoSelect = async (file: File | null) => {
    if (!file) return;
    const data = await fileToBase64(file);
    setLoadPhoto({ data, name: file.name });
  };

  const clearPhotos = () => { setLoadPhoto(null); };

  // Load pay codes and PA projects for cross-link dropdowns
  useEffect(() => {
    if (TABLES.payCodes) {
      const FPC = FIELDS.payCodes;
      queryRecords(TABLES.payCodes, {
        filters: FPC.status ? [{ field: FPC.status, operator: 'eq', value: 'active' }] : [],
        limit: 200,
      }).then(res => {
        setPayCodeOptions(res.records.map((r: any) => {
          const name = String(r[FPC.name] ?? '');
          const rate = r[FPC.rate] ? `$${r[FPC.rate]}/${r[FPC.unit] ?? 'unit'}` : '';
          return { value: String(r.record_id), label: rate ? `${name} — ${rate}` : name };
        }));
      }).catch(() => {});
    }
    if (TABLES.paProjects) {
      const FPP = FIELDS.paProjects;
      queryRecords(TABLES.paProjects, { limit: 200 }).then(res => {
        setPaProjectOptions(res.records.map((r: any) => ({
          value: String(r.record_id),
          label: `${r[FPP.projectNumber] ?? '---'} — ${r[FPP.title] ?? 'Untitled'}`,
        })));
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    loadContractors(); loadCrews(); loadTrucks(); loadZones(); loadDisposalSites();
    if (TABLES.debrisMonitors) {
      const FM = FIELDS.debrisMonitors;
      fetchTable(TABLES.debrisMonitors, { limit: 200 }).then((rows: any[]) => {
        const seen = new Set<string>();
        const opts: { value: string; label: string }[] = [];
        for (const m of rows) {
          if (String(m[FM.status] ?? 'active') !== 'active') continue;
          const name = String(m[FM.fullName] ?? '').trim();
          if (!name || seen.has(name)) continue;
          seen.add(name);
          const company = String(m[FM.company] ?? '');
          opts.push({ value: name, label: company ? `${name} — ${company}` : name });
        }
        setMonitorOptions(opts);
      }).catch(() => {});
    }
  }, [loadContractors, loadCrews, loadTrucks, loadZones, loadDisposalSites]);
  usePolling(() => { loadTrucks(); loadDisposalSites(); });

  const iTickets = tickets;
  const iContractors = incidentId ? contractors : [];
  const iTrucks = incidentId ? trucks : [];
  const iCrews = incidentId ? crews : [];
  const iZones = incidentId ? zones : [];
  const iSites = incidentId ? disposalSites : [];

  const contractorSelect = useMemo(() => iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })), [iContractors]);
  const truckSelect = useMemo(() => iTrucks.map(t => ({ value: String(t.id), label: `${t.truckNumber} (${t.cubicYardCapacity || '?'} CY)` })), [iTrucks]);
  // Lookup map: truckId (number) → truckNumber string
  const truckMap = useMemo(() => {
    const m = new Map<number, string>();
    iTrucks.forEach(tr => { if (tr.id) m.set(Number(tr.id), tr.truckNumber || ''); });
    return m;
  }, [iTrucks]);
  const crewSelect = useMemo(() => iCrews.map(c => ({ value: String(c.id), label: c.crewName || `#${c.id}` })), [iCrews]);
  const zoneSelect = useMemo(() => iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `#${z.id}` })), [iZones]);
  const siteSelect = useMemo(() => iSites.map(s => ({ value: String(s.id), label: s.siteName || `#${s.id}` })), [iSites]);

  // Truck IDs/numbers whose cert was uploaded for the CURRENT incident
  // Only matches trucks where certIncidentId in TRMETA matches the active incident.
  // Certs uploaded before this field was introduced won't show links until re-uploaded via PackageProcessor.
  const certifiedTruckKeys = useMemo(() => {
    const keys = new Set<string>();
    if (!incidentId) return keys;
    iTrucks.forEach(tr => {
      const raw = (tr as any).notes || '';
      const mm = raw.match(TRMETA_RE);
      if (!mm) return;
      try {
        const meta = JSON.parse(mm[1]);
        if (meta.certIncidentId === incidentId) {
          if (tr.id) keys.add(String(tr.id));
          if (tr.truckNumber) keys.add(tr.truckNumber);
        }
      } catch {}
    });
    return keys;
  }, [iTrucks, incidentId]);

  // A ticket is "complete" when it has all FEMA billing fields populated (cert is separate)
  const isTicketComplete = (t: DebrisLoadTicket): boolean => {
    const hasTruck = !!(t.truckId && t.truckId !== '0') || !!t.truckNumber;
    const hasType = !!t.debrisType;
    const hasMeasurement = parseFloat(t.calculatedCubicYards || '0') > 0
      || parseFloat(t.unitCount || '0') > 0
      || parseFloat(t.weight || '0') > 0;
    const hasSite = !!t.disposalSiteId;
    const notDraft = t.status !== 'draft' && t.status !== 'voided';
    return hasTruck && hasType && hasMeasurement && hasSite && notDraft;
  };

  const filtered = useMemo(() => {
    let list = iTickets;
    if (statusFilter && statusFilter !== 'all') list = list.filter(t => t.status === statusFilter);
    if (typeFilter && typeFilter !== 'all') list = list.filter(t => t.debrisType === typeFilter);
    if (completeFilter === 'complete') list = list.filter(t => isTicketComplete(t));
    if (completeFilter === 'incomplete') list = list.filter(t => !isTicketComplete(t));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        (t.ticketNumber || '').toLowerCase().includes(q) ||
        (truckMap.get(Number(t.truckId)) || t.truckNumber || '').toLowerCase().includes(q) ||
        (t.pickupAddress || '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (b.id || 0) - (a.id || 0); // newest first
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const numA = Number(va); const numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB) && va !== '' && vb !== '') return (numA - numB) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iTickets, statusFilter, typeFilter, completeFilter, search, sortField, sortDir, certifiedTruckKeys]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [statusFilter, typeFilter, completeFilter, search]);

  const zoneName = (id: string) => zones.find(z => String(z.id) === id)?.zoneName ?? id;
  const siteName = (id: string) => disposalSites.find(s => String(s.id) === id)?.siteName ?? id;
  // Aggregate totals from pickup stops
  const aggregateFromStops = (stopsJson: string, prev: Partial<DebrisLoadTicket>) => {
    try {
      const stops: any[] = JSON.parse(stopsJson || '[]');
      let totalCY = 0;
      let totalUnits = 0;
      let totalWeight = 0;
      for (const s of stops) {
        const cfg = s.debrisType ? DEBRIS_TYPES[s.debrisType as DebrisType] : null;
        const mode = cfg?.measurementMode || 'volumetric';
        if (mode === 'volumetric') totalCY += parseFloat(s.unitCount || '0');
        else if (mode === 'unit') totalUnits += parseFloat(s.unitCount || '0');
        else if (mode === 'weight') totalWeight += parseFloat(s.weight || '0');
      }
      const patch: Partial<DebrisLoadTicket> = {};
      if (totalCY) {
        patch.calculatedCubicYards = totalCY.toString();
        patch.loadPercentage = computeLoadPct(totalCY.toString(), prev.truckId ?? '');
      } else if (!totalCY && !prev.calculatedCubicYards) {
        patch.calculatedCubicYards = '';
        patch.loadPercentage = '';
      }
      if (totalUnits) patch.unitCount = totalUnits.toString();
      if (totalWeight) patch.weight = totalWeight.toString();
      return patch;
    } catch { return {}; }
  };

  const setField = (key: keyof DebrisLoadTicket, value: string) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      // If stops changed, re-aggregate totals
      if (key === 'pickupStopsJson') {
        const agg = aggregateFromStops(value, next);
        return { ...next, ...agg };
      }
      return next;
    });
  };

  // Auto-generate ticket number
  const nextTicket = () => {
    const max = iTickets.reduce((m, t) => {
      const n = parseInt(t.ticketNumber?.replace(/\D/g, '') || '0');
      return n > m ? n : m;
    }, 0);
    return `T-${String(max + 1).padStart(5, '0')}`;
  };

  // Compute load% from CY and truck capacity (derived, not stored independently)
  const computeLoadPct = (cy: string, truckId: string) => {
    const truck = trucks.find(t => String(t.id) === truckId);
    if (!truck) return '';
    const cap = parseFloat(truck.cubicYardCapacity || '0');
    const cyVal = parseFloat(cy || '0');
    return cap && cyVal ? Math.round((cyVal / cap) * 100).toString() : '';
  };

  const handleTruckChange = (truckId: string) => {
    const truck = trucks.find(t => String(t.id) === truckId);
    setForm(prev => ({
      ...prev,
      truckId,
      truckNumber: truck?.truckNumber ?? '',
      contractorId: truck?.contractorId ?? prev.contractorId ?? '',
      loadPercentage: computeLoadPct(prev.calculatedCubicYards ?? '', truckId),
    }));
  };

  const resetLinkedDir = () => {
    setLinkedFacilityId(null); setLinkedFacilityName(''); setFacilityQuery(''); setFacilityResults([]);
    setLinkedMonitorPersonId(null); setLinkedMonitorPersonName(''); setMonitorPersonQuery(''); setMonitorPersonResults([]);
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY, ticketNumber: nextTicket() });
    clearPhotos();
    resetLinkedDir();
    setModalOpen(true);
  };
  const openEdit = (t: DebrisLoadTicket) => {
    setEditTarget(t);
    setForm({ ...t });
    clearPhotos();
    resetLinkedDir();
    if (t.facilityId) {
      setLinkedFacilityId(Number(t.facilityId));
      setLinkedFacilityName(`Directory Facility #${t.facilityId}`);
    }
    setModalOpen(true);
  };

  // Resize stop photos to tiny thumbnails before persisting (Ignatius TEXT fields cap at ~65K)
  const compactStopsForSave = async (stopsJson: string): Promise<string> => {
    try {
      const stops: any[] = JSON.parse(stopsJson || '[]');
      const compacted = await Promise.all(stops.map(async (s: any) => {
        const out = { ...s };
        // Resize photos to 800px — stored in notes meta (separate TEXT field), not stops JSON
        if (out.beforePhotoData && out.beforePhotoData.length > 10000) {
          try {
            const { resizeBase64ToThumb } = await import('../intake/lib/helpers');
            out.beforePhotoData = await resizeBase64ToThumb(out.beforePhotoData, 800, 0.7);
          } catch { out.beforePhotoData = ''; }
        }
        if (out.afterPhotoData && out.afterPhotoData.length > 10000) {
          try {
            const { resizeBase64ToThumb } = await import('../intake/lib/helpers');
            out.afterPhotoData = await resizeBase64ToThumb(out.afterPhotoData, 800, 0.7);
          } catch { out.afterPhotoData = ''; }
        }
        return out;
      }));
      return JSON.stringify(compacted);
    } catch { return stopsJson; }
  };

  const handleSave = async () => {
    if (!form.ticketNumber?.trim()) {
      notifications.show({ message: 'Ticket number is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      // Compact stop photos before saving
      const compactedStops = form.pickupStopsJson
        ? await compactStopsForSave(form.pickupStopsJson)
        : '';
      const payload = {
        ...form,
        incidentId,
        pickupStopsJson: compactedStops,
        ...(FIELDS.debrisLoadTickets.facilityId && linkedFacilityId ? { facilityId: String(linkedFacilityId) } : {}),
        ...(FIELDS.debrisLoadTickets.monitorPersonId && linkedMonitorPersonId ? { monitorPersonId: String(linkedMonitorPersonId) } : {}),
        ...(FIELDS.debrisLoadTickets.contractorOrgId && linkedContractorOrgId ? { contractorOrgId: String(linkedContractorOrgId) } : {}),
        ...(FIELDS.debrisLoadTickets.payCodeId && (form as any).payCodeId ? { payCodeId: (form as any).payCodeId } : {}),
        ...(FIELDS.debrisLoadTickets.paProjectId && (form as any).paProjectId ? { paProjectId: (form as any).paProjectId } : {}),
      };
      let ticketId: number;
      if (editTarget) {
        await updateLoadTicket(editTarget.id, payload);
        ticketId = editTarget.id;
        notifications.show({ message: 'Ticket updated', color: 'var(--color-success)' });
      } else {
        const created = await createLoadTicket(payload);
        ticketId = created?.id ?? 0;
        notifications.show({ message: 'Ticket created', color: 'var(--color-success)' });
      }

      // Store load photo in ticket meta (photoLoadUrl has no V2 column)
      if (ticketId && loadPhoto) {
        await updateLoadTicket(ticketId, { photoLoadUrl: loadPhoto.data });
      }

      invalidateTicketCache();
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteLoadTicket(deleteTarget.id);
      notifications.show({ message: `Ticket ${deleteTarget.ticketNumber} deleted`, color: '#939598' });
      invalidateTicketCache();
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleClearAll = async () => {
    setClearingAll(true);
    try {
      const ids = allTickets.map(t => t.id);
      await Promise.all(ids.map(id => deleteLoadTicket(id)));
      notifications.show({ message: `${ids.length} tickets deleted`, color: '#939598' });
      invalidateTicketCache();
      setClearAllOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Clear failed', color: 'var(--color-error)' });
    } finally { setClearingAll(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Load Tickets</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
            onClick={() => exportCSV('debris-tickets',
              ['Ticket #', 'Truck', 'Debris Type', 'Load %', 'CY', 'Zone', 'Disposal Site', 'Status', 'Subcontractor', 'CLIN Code', 'Distance (mi)', 'Rate', 'Extended Cost', 'Pickup Time', 'Pickup Address', 'Loading Monitor'],
              filtered.map(t => [t.ticketNumber, truckMap.get(Number(t.truckId)) || t.truckNumber, DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType, t.loadPercentage, t.calculatedCubicYards, t.zoneId ? zoneName(t.zoneId) : '', t.disposalSiteId ? siteName(t.disposalSiteId) : '', t.status, t.subcontractorName || '', t.clinCode || '', t.distanceMiles || '', t.transactionRate || '', t.extendedCost || '', t.pickupTime, t.pickupAddress, t.loadingMonitorName])
            )}>
            Export CSV
          </Button>
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingTickets} onClick={() => invalidateTicketCache()}>Refresh</Button>
          {canEdit && allTickets.length > 0 && (
            <Button size="xs" color="var(--color-error)" variant="light" leftSection={<IconTrash size={14} />}
              onClick={() => setClearAllOpen(true)}>
              Clear All ({allTickets.length})
            </Button>
          )}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search ticket #, truck, address..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
        <Select data={[{ value: 'all', label: 'All Types' }, ...DEBRIS_TYPE_SELECT]}
          value={typeFilter ?? 'all'} onChange={v => setTypeFilter(v)} size="xs" w={180} />
        <Select data={[
          { value: 'all', label: 'All Records' },
          { value: 'complete', label: '✓ Complete only' },
          { value: 'incomplete', label: '⚠ Incomplete only' },
        ]} value={completeFilter ?? 'all'} onChange={v => setCompleteFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} ticket{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 16px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('ticketNumber')}>Ticket{sortIcon('ticketNumber')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('truckNumber')}>Truck{sortIcon('truckNumber')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('debrisType')}>Type{sortIcon('debrisType')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('cubicYards')}>CY{sortIcon('cubicYards')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('subcontractorName')}>Subcontractor{sortIcon('subcontractorName')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('clinCode')}>CLIN{sortIcon('clinCode')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('distanceMiles')}>Dist (mi){sortIcon('distanceMiles')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('transactionRate')}>Rate{sortIcon('transactionRate')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('extendedCost')}>Ext. Cost{sortIcon('extendedCost')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('pickupTime')}>Pickup{sortIcon('pickupTime')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={12} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingTickets ? 'Loading...' : 'No tickets found'}
                </td></tr>
              )}
              {pageRows.map(t => {
                const isLocked = ['verified', 'delivered', 'voided'].includes(t.status) && !isAdmin;
                return (
                <tr key={t.id} style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                  onClick={() => navigate(`/debris/ticket/${t.id}`)}>
                  <td style={{ padding: '8px 16px', fontWeight: 500 }}>
                    <Group gap={6} wrap="nowrap">
                      {isTicketComplete(t)
                        ? <IconCircleCheck size={14} style={{ color: 'var(--color-success)', flexShrink: 0 }} title="Complete" />
                        : <IconAlertCircle size={14} style={{ color: '#C68700', flexShrink: 0 }} title="Incomplete" />}
                      <Text size="sm" fw={500}>{t.ticketNumber || `#${t.id}`}</Text>
                      {t.status === 'draft' && <Badge size="xs" color="#939598" variant="light">Draft</Badge>}
                    </Group>
                  </td>
                  {(() => {
                    const displayTruck = truckMap.get(Number(t.truckId)) || t.truckNumber || '';
                    const validTruckId = t.truckId && t.truckId !== '0' && t.truckId !== '';
                    const lookupKey = validTruckId ? t.truckId : (t.truckNumber || '');
                    const hasCertDoc = !!lookupKey && certifiedTruckKeys.has(lookupKey);
                    return (
                      <td style={{ padding: '8px 16px' }}
                        onClick={hasCertDoc ? (e: React.MouseEvent) => { e.stopPropagation(); viewTruckCert(lookupKey); } : undefined}>
                        {hasCertDoc ? (
                          <span style={{ cursor: 'pointer', color: 'var(--color-primary-700)', textDecoration: 'underline', textDecorationStyle: 'dotted', fontSize: '0.85rem' }}>
                            {certPdfLoading === lookupKey ? 'Loading...' : (displayTruck || '-')}
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.85rem' }}>{displayTruck || '-'}</span>
                        )}
                      </td>
                    );
                  })()}
                  <td style={{ padding: '8px 16px' }}>
                    <Badge size="sm" color={DEBRIS_TYPE_COLORS[t.debrisType as DebrisType] ?? '#939598'}>
                      {DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType ?? '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600 }}>
                    {t.measurementMode === 'unit'
                      ? `${t.unitCount || 0} ${DEBRIS_TYPES[t.debrisType as DebrisType]?.unitLabel || 'units'}`
                      : t.measurementMode === 'weight'
                        ? `${parseFloat(t.weight || '0').toLocaleString()} ${t.weightUnit || 'lbs'}`
                        : `${parseFloat(t.calculatedCubicYards || '0').toLocaleString()} CY`
                    }
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <Badge size="sm" color={STATUS_COLORS[t.status] ?? '#939598'}>{t.status?.replace(/_/g, ' ')}</Badge>
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <Text size="xs">{t.subcontractorName || '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <Text size="xs">{t.clinCode ? clinLabel(t.clinCode) : '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                    <Text size="xs">{t.distanceMiles ? `${parseFloat(t.distanceMiles).toFixed(1)}` : '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                    <Text size="xs">{t.transactionRate ? `$${parseFloat(t.transactionRate).toFixed(2)}` : '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                    <Text size="xs" fw={600}>{t.extendedCost ? `$${parseFloat(t.extendedCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <Text size="xs">{t.pickupTime || '-'}</Text>
                    <Text size="xs" c="dimmed">{t.pickupAddress}</Text>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', width: 84 }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 24px)', gap: 2, justifyContent: 'end' }}>
                      {/* Edit */}
                      {canEdit && (!['verified', 'delivered', 'voided'].includes(t.status) || isAdmin) ? (
                        <ActionIcon size="sm" variant="subtle"
                          color={['verified', 'delivered', 'voided'].includes(t.status) ? '#F86A0B' : undefined}
                          title={['verified', 'delivered', 'voided'].includes(t.status) ? 'Admin Edit' : 'Edit'}
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); openEdit(t); }}>
                          <IconPencil size={14} />
                        </ActionIcon>
                      ) : <span />}
                      {/* Delete */}
                      {canEdit ? (
                        <ActionIcon size="sm" variant="subtle" color="var(--color-error)" title="Delete"
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDeleteTarget(t); }}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      ) : <span />}
                      {/* Slot 5: Undo Void */}
                      {isAdmin && t.status === 'voided' ? (
                        <ActionIcon size="sm" variant="subtle" color="var(--color-primary-700)" title="Undo Void (Admin)"
                          onClick={async (e: React.MouseEvent) => {
                            e.stopPropagation();
                            await updateLoadTicket(t.id, { status: 'disputed' as any, notes: `${t.notes ? t.notes + ' | ' : ''}Void reversed by admin at ${new Date().toLocaleTimeString()}` });
                            notifications.show({ message: `${t.ticketNumber} restored — needs re-verification`, color: 'var(--color-primary-700)' });
                            invalidateTicketCache();
                          }}>
                          <IconArrowBackUp size={14} />
                        </ActionIcon>
                      ) : <span />}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Load Ticket' : 'New Load Ticket'} centered size="xl">
        <Stack>
          <Text size="sm" fw={600} c="dimmed">Ticket Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Ticket Number" required value={form.ticketNumber ?? ''}
              onChange={e => setField('ticketNumber', e.target.value)} />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as TicketStatus) ?? 'in_transit'}
              onChange={v => setField('status', v ?? 'in_transit')} />
            <Select label="Debris Type" data={DEBRIS_TYPE_SELECT}
              value={form.debrisType || null} onChange={v => setField('debrisType', v ?? '')} searchable />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Resources</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Select label="Truck" data={truckSelect} value={form.truckId || null}
              onChange={v => handleTruckChange(v ?? '')} searchable clearable />
            <Select label="Crew" data={crewSelect} value={form.crewId || null}
              onChange={v => setField('crewId', v ?? '')} searchable clearable />
            <Select label="Contractor (Roster)" data={contractorSelect} value={form.contractorId || null}
              onChange={v => setField('contractorId', v ?? '')} searchable clearable />
          </SimpleGrid>

          {/* Directory: link to provisioned Organizations table */}
          {TABLES.organizations && (
            <div>
              <Text size="xs" fw={600} mb={4} c="dimmed">Link Contractor to Directory Organization</Text>
              {linkedContractorOrgId ? (
                <Group gap={6}>
                  <Badge color="var(--data-viz-650)" variant="light">
                    {linkedContractorOrgName}
                  </Badge>
                  <Text size="xs" c="dimmed">linked from directory</Text>
                  <ActionIcon size="xs" variant="subtle" color="#939598"
                    onClick={() => { setLinkedContractorOrgId(null); setLinkedContractorOrgName(''); setContractorOrgQuery(''); setContractorOrgResults([]); }}>
                    <IconX size={10} />
                  </ActionIcon>
                </Group>
              ) : (
                <div style={{ position: 'relative' }}>
                  <TextInput
                    size="xs"
                    placeholder="Search directory organizations..."
                    value={contractorOrgQuery}
                    onChange={e => handleContractorOrgQuery(e.target.value)}
                    rightSection={contractorOrgSearching ? <Text size="xs" c="dimmed">...</Text> : null}
                  />
                  {contractorOrgResults.length > 0 && (
                    <Paper withBorder shadow="sm" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, maxHeight: 200, overflowY: 'auto' }}>
                      {contractorOrgResults.map(o => (
                        <div key={o.id}
                          style={{ padding: '8px 8px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f0f0f0' }}
                          onMouseDown={() => {
                            setLinkedContractorOrgId(o.id);
                            setLinkedContractorOrgName(o.name);
                            setContractorOrgQuery('');
                            setContractorOrgResults([]);
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>{o.name}</div>
                          {o.city && <div style={{ fontSize: 12, color: '#888' }}>{o.city}</div>}
                        </div>
                      ))}
                    </Paper>
                  )}
                </div>
              )}
            </div>
          )}

          <SimpleGrid cols={{ base: 1, sm: editTarget ? 3 : 1 }} spacing="sm">
            <Select label="Pickup Zone" data={zoneSelect} value={form.zoneId || null}
              onChange={v => setField('zoneId', v ?? '')} searchable clearable />
            {/* Disposal site + monitor only shown when editing — at creation time
                the crew doesn't know these yet. They get set during verification. */}
            {editTarget && (
              <>
                <Select label="Disposal Site" data={siteSelect} value={form.disposalSiteId || null}
                  onChange={v => setField('disposalSiteId', v ?? '')} searchable clearable />
              </>
            )}
          </SimpleGrid>

          {/* Directory: link to provisioned Facilities table */}
          {editTarget && TABLES.facilities && (
            <div>
              <Text size="xs" fw={600} mb={4} c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <IconBuilding size={12} /> Link Disposal Site to Directory Facility
              </Text>
              {linkedFacilityId ? (
                <Group gap={6}>
                  <Badge color="#03AC9E" variant="light" leftSection={<IconBuilding size={10} />}
                    style={{ cursor: 'default' }}>
                    {linkedFacilityName}
                  </Badge>
                  <Text size="xs" c="dimmed">linked from directory</Text>
                  <ActionIcon size="xs" variant="subtle" color="#939598"
                    onClick={() => { setLinkedFacilityId(null); setLinkedFacilityName(''); setFacilityQuery(''); setFacilityResults([]); }}>
                    <IconX size={10} />
                  </ActionIcon>
                </Group>
              ) : (
                <div style={{ position: 'relative' }}>
                  <TextInput
                    size="xs"
                    placeholder="Search directory facilities..."
                    value={facilityQuery}
                    onChange={e => handleFacilityQuery(e.target.value)}
                    leftSection={<IconBuilding size={12} />}
                    rightSection={facilitySearching ? <Text size="xs" c="dimmed">…</Text> : null}
                    style={{ maxWidth: 320 }}
                  />
                  {facilityResults.length > 0 && (
                    <Paper withBorder shadow="md" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, minWidth: 280, maxHeight: 200, overflowY: 'auto' }}>
                      {facilityResults.map(f => (
                        <div key={f.id}
                          style={{ padding: '8px 8px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}
                          onMouseDown={() => {
                            setLinkedFacilityId(f.id);
                            setLinkedFacilityName(f.name);
                            setFacilityQuery('');
                            setFacilityResults([]);
                          }}>
                          <Text size="xs" fw={500}>{f.name}</Text>
                          {(f.address || f.city) && <Text size="xs" c="dimmed">{[f.address, f.city].filter(Boolean).join(', ')}</Text>}
                        </div>
                      ))}
                    </Paper>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── PA Cross-Links ───────────────────────────────── */}
          {(TABLES.payCodes || TABLES.paProjects) && (
            <>
              <Text size="sm" fw={600} c="dimmed">PA Program Links</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                {TABLES.payCodes && (
                  <Select
                    label="Pay Code"
                    description="Rate schedule for FEMA reimbursement"
                    data={payCodeOptions}
                    value={(form as any).payCodeId || null}
                    onChange={v => setField('payCodeId' as any, v ?? '')}
                    searchable clearable
                    placeholder="Select pay code..."
                  />
                )}
                {TABLES.paProjects && (
                  <Select
                    label="PA Project"
                    description="Links ticket cost to a PA project on verification"
                    data={paProjectOptions}
                    value={(form as any).paProjectId || null}
                    onChange={v => setField('paProjectId' as any, v ?? '')}
                    searchable clearable
                    placeholder="Select project..."
                  />
                )}
              </SimpleGrid>
            </>
          )}

          {/* ── Totals (auto-aggregated from stops) ─────────────── */}
          {(() => {
            const stops: any[] = (() => { try { return JSON.parse(form.pickupStopsJson || '[]'); } catch { return []; } })();
            const hasCY = parseFloat(form.calculatedCubicYards || '0') > 0;
            const hasUnits = parseFloat(form.unitCount || '0') > 0;
            const hasWeight = parseFloat(form.weight || '0') > 0;
            const hasStops = stops.length > 0;
            return (
              <>
                {(hasCY || hasUnits || hasWeight) && (
                  <>
                    <Text size="sm" fw={600} c="dimmed">Totals {hasStops ? '(from stops)' : ''}</Text>
                    <Group gap="lg">
                      {hasCY && (
                        <div>
                          <Text size="lg" fw={700}>{parseFloat(form.calculatedCubicYards || '0').toLocaleString()} CY</Text>
                          {form.truckId && form.loadPercentage && (
                            <Text size="xs" c="dimmed">{form.loadPercentage}% of truck capacity</Text>
                          )}
                        </div>
                      )}
                      {hasUnits && <Text size="lg" fw={700}>{parseFloat(form.unitCount || '0').toLocaleString()} units</Text>}
                      {hasWeight && <Text size="lg" fw={700}>{parseFloat(form.weight || '0').toLocaleString()} {form.weightUnit || 'lbs'}</Text>}
                    </Group>
                  </>
                )}
                {!hasStops && !hasCY && !hasUnits && !hasWeight && (
                  <Text size="sm" c="dimmed" fs="italic">Add pickup stops below to calculate totals</Text>
                )}
              </>
            );
          })()}

          {/* ── Pickup Stops ─────────────────────────────────── */}
          <Group justify="space-between">
            <Text size="sm" fw={600} c="dimmed">Pickup Stops</Text>
            <Button size="compact-xs" variant="light" leftSection={<IconPlus size={12} />}
              onClick={() => {
                const stops = JSON.parse(form.pickupStopsJson || '[]');
                stops.push({
                  id: `stop-${Date.now()}`,
                  address: '',
                  lat: '', lng: '',
                  debrisType: form.debrisType || '',
                  unitCount: '', unitSubcategory: '', diameterRange: '',
                  weight: '', weightUnit: 'lbs',
                  notes: '',
                  timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
                });
                setField('pickupStopsJson', JSON.stringify(stops));
              }}>
              Add Stop
            </Button>
          </Group>
          {(() => {
            let stops: any[] = [];
            try { stops = JSON.parse(form.pickupStopsJson || '[]'); } catch { /* ignore */ }
            if (stops.length === 0) return (
              <Text size="xs" c="dimmed" ta="center" py="sm">No stops yet. Click "Add Stop" or use Field Pickup to build a load from the field.</Text>
            );
            return (
              <Stack gap="xs">
                {stops.map((stop: any, idx: number) => {
                  const stopCfg = stop.debrisType ? DEBRIS_TYPES[stop.debrisType as DebrisType] : null;
                  const updateStop = (patch: Record<string, unknown>) => {
                    const updated = [...stops];
                    updated[idx] = { ...stop, ...patch };
                    setField('pickupStopsJson', JSON.stringify(updated));
                  };
                  const hasCoords = stop.lat && stop.lng;
                  const hasBeforePhoto = !!stop.beforePhotoData;
                  const hasAfterPhoto = !!stop.afterPhotoData;
                  return (
                    <Paper key={stop.id || idx} p="sm" radius="sm" withBorder style={{ background: '#F8F8F8' }}>
                      <Group justify="space-between" mb={6}>
                        <Group gap={6}>
                          <Badge size="xs" variant="light">Stop {idx + 1}</Badge>
                          {hasCoords && <Badge size="xs" color="var(--color-primary-700)" variant="light" leftSection={<IconMapPin size={9} />}>{parseFloat(stop.lat).toFixed(4)}, {parseFloat(stop.lng).toFixed(4)}</Badge>}
                          {hasBeforePhoto && <Badge size="xs" color="var(--color-success)" variant="light" leftSection={<IconPhoto size={9} />}>Before</Badge>}
                          {hasAfterPhoto && <Badge size="xs" color="var(--color-success)" variant="light" leftSection={<IconPhoto size={9} />}>After</Badge>}
                        </Group>
                        <ActionIcon size="xs" variant="subtle" color="var(--color-error)" onClick={() => {
                          const updated = stops.filter((_: any, i: number) => i !== idx);
                          setField('pickupStopsJson', JSON.stringify(updated));
                        }}>
                          <IconX size={10} />
                        </ActionIcon>
                      </Group>

                      {/* Row 1: Address + GPS + Debris Type */}
                      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs" mb="xs">
                        <Group gap={4} style={{ flex: 1, position: 'relative' }}>
                          <TextInput size="xs" placeholder="Address" style={{ flex: 1 }}
                            value={stop.address || ''}
                            onChange={e => {
                              updateStop({ address: e.target.value });
                              fetchStopAddr(e.target.value, idx);
                            }}
                            onFocus={() => { if (stopAddrResults.length > 0) setStopAddrActiveIdx(idx); }}
                            onBlur={() => setTimeout(() => setStopAddrActiveIdx(-1), 200)} />
                          {stopAddrActiveIdx === idx && stopAddrResults.length > 0 && (
                            <Paper withBorder shadow="md" style={{
                              position: 'absolute', top: '100%', left: 0, right: 40, zIndex: 50,
                              maxHeight: 180, overflowY: 'auto',
                            }}>
                              {stopAddrResults.map((r, ri) => (
                                <div key={ri} style={{ padding: '8px 8px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}
                                  onMouseDown={() => {
                                    updateStop({ address: r.label, lat: r.lat, lng: r.lng });
                                    setStopAddrResults([]);
                                    setStopAddrActiveIdx(-1);
                                  }}>
                                  {r.label}
                                </div>
                              ))}
                            </Paper>
                          )}
                          <ActionIcon size="sm" variant="light" color="var(--color-primary-700)" title="Get GPS"
                            onClick={() => {
                              if (!navigator.geolocation) { notifications.show({ message: 'GPS not available', color: '#C68700' }); return; }
                              navigator.geolocation.getCurrentPosition(
                                pos => updateStop({ lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }),
                                err => notifications.show({ message: `GPS: ${err.message}`, color: 'var(--color-error)' }),
                                { enableHighAccuracy: true, timeout: 10000 },
                              );
                            }}>
                            <IconCurrentLocation size={14} />
                          </ActionIcon>
                        </Group>
                        <Select size="xs" placeholder="Debris type"
                          data={DEBRIS_TYPE_SELECT}
                          value={stop.debrisType || null}
                          onChange={v => updateStop({ debrisType: v ?? '' })} searchable />
                        {/* Every type gets a quantity/measurement field */}
                        {(!stopCfg || stopCfg.measurementMode === 'volumetric') && (
                          <TextInput size="xs" placeholder="Est. CY or # of piles" type="number"
                            value={stop.unitCount || ''}
                            onChange={e => updateStop({ unitCount: e.target.value })} />
                        )}
                        {stopCfg?.measurementMode === 'unit' && (
                          <TextInput size="xs" placeholder={`Qty (${stopCfg.unitLabel || 'units'})`} type="number"
                            value={stop.unitCount || ''}
                            onChange={e => updateStop({ unitCount: e.target.value })} />
                        )}
                        {stopCfg?.measurementMode === 'weight' && (
                          <TextInput size="xs" placeholder="Weight" type="number"
                            value={stop.weight || ''}
                            onChange={e => updateStop({ weight: e.target.value })} />
                        )}
                      </SimpleGrid>

                      {/* Row 2: Photos + Notes */}
                      <Group gap="xs" align="flex-start">
                        {/* Before photo */}
                        {hasBeforePhoto ? (
                          <div style={{ position: 'relative', width: 64, height: 48 }}>
                            <img src={stop.beforePhotoData} alt="Before"
                              style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                              onClick={() => { setLightboxSrc(stop.beforePhotoData); setLightboxLabel(`Stop ${idx + 1} — Before`); }} />
                            <ActionIcon size={12} variant="filled" color="var(--color-error)"
                              style={{ position: 'absolute', top: -3, right: -3, opacity: 0.6 }}
                              title="Remove before photo"
                              onClick={() => { if (window.confirm('Remove this before photo?')) updateStop({ beforePhotoData: null }); }}>
                              <IconX size={7} />
                            </ActionIcon>
                          </div>
                        ) : (
                          <label style={{ cursor: 'pointer' }}>
                            <Paper p={6} radius="sm" withBorder
                              style={{ width: 64, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: '#fff' }}>
                              <IconCamera size={16} color="#888" />
                              <Text size="xs" c="dimmed" lh={1}>Before</Text>
                            </Paper>
                            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                              onChange={async e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const data = await fileToBase64(file);
                                updateStop({ beforePhotoData: data });
                              }} />
                          </label>
                        )}

                        {/* After photo */}
                        {hasAfterPhoto ? (
                          <div style={{ position: 'relative', width: 64, height: 48 }}>
                            <img src={stop.afterPhotoData} alt="After"
                              style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                              onClick={() => { setLightboxSrc(stop.afterPhotoData); setLightboxLabel(`Stop ${idx + 1} — After`); }} />
                            <ActionIcon size={12} variant="filled" color="var(--color-error)"
                              style={{ position: 'absolute', top: -3, right: -3, opacity: 0.6 }}
                              title="Remove after photo"
                              onClick={() => { if (window.confirm('Remove this after photo?')) updateStop({ afterPhotoData: null }); }}>
                              <IconX size={7} />
                            </ActionIcon>
                          </div>
                        ) : (
                          <label style={{ cursor: 'pointer' }}>
                            <Paper p={6} radius="sm" withBorder
                              style={{ width: 64, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: '#fff' }}>
                              <IconCamera size={16} color="#888" />
                              <Text size="xs" c="dimmed" lh={1}>After</Text>
                            </Paper>
                            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                              onChange={async e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const data = await fileToBase64(file);
                                updateStop({ afterPhotoData: data });
                              }} />
                          </label>
                        )}

                        {/* Notes inline */}
                        <TextInput size="xs" placeholder="Notes..." style={{ flex: 1 }}
                          value={stop.notes || ''}
                          onChange={e => updateStop({ notes: e.target.value })} />
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            );
          })()}

          <TextInput label="Date" type="date"
            value={form.pickupTime ? form.pickupTime.slice(0, 10) : new Date().toISOString().slice(0, 10)}
            onChange={e => setField('pickupTime', e.target.value || '')}
            style={{ maxWidth: 220 }} />

          {/* FEMA Chain of Custody — Pickup Signatures */}
          <Divider label="Pickup — Loading Site Monitor" labelPosition="left" />

          {/* Directory: link monitor to provisioned People table */}
          {TABLES.people && (
            <div>
              <Text size="xs" fw={600} mb={4} c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <IconId size={12} /> Link Monitor to Directory Person
              </Text>
              {linkedMonitorPersonId ? (
                <Group gap={6}>
                  <Badge color="var(--color-primary-700)" variant="light" leftSection={<IconId size={10} />}
                    style={{ cursor: 'default' }}>
                    {linkedMonitorPersonName}
                  </Badge>
                  <Text size="xs" c="dimmed">linked from directory</Text>
                  <ActionIcon size="xs" variant="subtle" color="#939598"
                    onClick={() => { setLinkedMonitorPersonId(null); setLinkedMonitorPersonName(''); setMonitorPersonQuery(''); setMonitorPersonResults([]); }}>
                    <IconX size={10} />
                  </ActionIcon>
                </Group>
              ) : (
                <div style={{ position: 'relative' }}>
                  <TextInput
                    size="xs"
                    placeholder="Search directory people..."
                    value={monitorPersonQuery}
                    onChange={e => handleMonitorPersonQuery(e.target.value)}
                    leftSection={<IconId size={12} />}
                    rightSection={monitorPersonSearching ? <Text size="xs" c="dimmed">…</Text> : null}
                    style={{ maxWidth: 320 }}
                  />
                  {monitorPersonResults.length > 0 && (
                    <Paper withBorder shadow="md" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, minWidth: 280, maxHeight: 200, overflowY: 'auto' }}>
                      {monitorPersonResults.map(p => (
                        <div key={p.id}
                          style={{ padding: '8px 8px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}
                          onMouseDown={() => {
                            setLinkedMonitorPersonId(p.id);
                            setLinkedMonitorPersonName(p.name);
                            // Auto-fill text monitor name if blank
                            if (!form.loadingMonitorName) setField('loadingMonitorName', p.name);
                            setMonitorPersonQuery('');
                            setMonitorPersonResults([]);
                          }}>
                          <Text size="xs" fw={500}>{p.name}</Text>
                          {(p.title || p.email) && <Text size="xs" c="dimmed">{[p.title, p.email].filter(Boolean).join(' · ')}</Text>}
                        </div>
                      ))}
                    </Paper>
                  )}
                </div>
              )}
            </div>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Loading Monitor" data={Array.from(new Map(monitorOptions.map(o => [o.value, o])).values())}
              value={form.loadingMonitorName || null}
              onChange={v => setField('loadingMonitorName', v ?? '')}
              searchable clearable placeholder="Select loading site monitor..." />
            <TextInput label="Load Estimate (%)" type="number" min={0} max={100}
              value={form.loadingLoadPct ?? ''}
              onChange={e => setField('loadingLoadPct', e.target.value)}
              placeholder="e.g. 75" />
          </SimpleGrid>
          <Switch
            label="Hand-loaded"
            description="FEMA auto-reduces hand-loaded trucks to 50% capacity"
            checked={form.handLoaded === 'true'}
            onChange={e => setField('handLoaded', e.currentTarget.checked ? 'true' : 'false')}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <SignaturePad label="Loading Monitor Signature" value={form.loadingMonitorSignature}
              onCapture={v => setField('loadingMonitorSignature', v)} />
            <SignaturePad label="Driver Signature (Pickup)" value={form.driverPickupSignature}
              onCapture={v => setField('driverPickupSignature', v)} />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Load Photo</Text>
          <div style={{ maxWidth: 200 }}>
            {loadPhoto ? (
              <div style={{ position: 'relative' }}>
                <img src={loadPhoto.data} alt="Load"
                  style={{ width: '100%', borderRadius: 4, maxHeight: 120, objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => { setLightboxSrc(loadPhoto.data); setLightboxLabel('Load Photo'); }} />
                <ActionIcon size="xs" color="var(--color-error)" variant="filled"
                  style={{ position: 'absolute', top: 2, right: 2 }}
                  onClick={() => setLoadPhoto(null)}>
                  <IconX size={10} />
                </ActionIcon>
              </div>
            ) : form.photoLoadUrl ? (
              <div style={{ position: 'relative' }}>
                <img src={form.photoLoadUrl} alt="Load"
                  style={{ width: '100%', borderRadius: 4, maxHeight: 120, objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => { setLightboxSrc(form.photoLoadUrl); setLightboxLabel('Load Photo'); }} />
                <Badge size="xs" style={{ position: 'absolute', bottom: 4, left: 4 }}>Saved</Badge>
              </div>
            ) : (
              <label style={{ cursor: 'pointer' }}>
                <Paper p="sm" radius="sm" withBorder
                  style={{ textAlign: 'center', background: '#F8F8F8', minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                  <IconCamera size={20} color="#888" />
                  <Text size="xs" c="dimmed">Photo of load</Text>
                </Paper>
                <input type="file" accept="image/*" capture="environment"
                  style={{ display: 'none' }}
                  onChange={e => handleLoadPhotoSelect(e.target.files?.[0] ?? null)} />
              </label>
            )}
          </div>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Ticket'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Clear all confirmation */}
      <Modal opened={clearAllOpen} onClose={() => setClearAllOpen(false)} title="Clear All Tickets" centered size="sm">
        <Stack>
          <Text size="sm">Delete all <strong>{allTickets.length} tickets</strong> for this incident? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setClearAllOpen(false)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={clearingAll} onClick={handleClearAll}>Delete All</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Ticket" centered size="sm">
        <Stack>
          <Text size="sm">Delete ticket <strong>{deleteTarget?.ticketNumber}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── View Detail Modal (read-only) ──────────────── */}
      <Modal opened={!!viewTicket} onClose={() => setViewTicket(null)}
        title={`Ticket Detail — ${viewTicket?.ticketNumber || ''}`} centered size="lg">
        {viewTicket && (() => {
          const vt = viewTicket;
          const vtCfg = vt.debrisType ? DEBRIS_TYPES[vt.debrisType as DebrisType] : null;
          const vtStops: any[] = (() => { try { return JSON.parse(vt.pickupStopsJson || '[]'); } catch { return []; } })();
          return (
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
                <div>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge color={STATUS_COLORS[vt.status] ?? '#939598'}>{TICKET_STATUS_LABELS[vt.status as TicketStatus] ?? vt.status}</Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Debris Type</Text>
                  <Badge color={DEBRIS_TYPE_COLORS[vt.debrisType as DebrisType] ?? '#939598'}>
                    {DEBRIS_TYPE_LABELS[vt.debrisType as DebrisType] ?? vt.debrisType ?? '-'}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Measurement</Text>
                  <Text size="sm" fw={600}>
                    {vt.calculatedCubicYards ? `${vt.calculatedCubicYards} CY` : ''}
                    {vt.unitCount ? `${vt.unitCount} ${vtCfg?.unitLabel || 'units'}` : ''}
                    {vt.weight ? `${vt.weight} ${vt.weightUnit || 'lbs'}` : ''}
                    {!vt.calculatedCubicYards && !vt.unitCount && !vt.weight ? '-' : ''}
                    {vt.loadPercentage ? ` (${vt.loadPercentage}%)` : ''}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Truck</Text>
                  <Text size="sm">{truckMap.get(Number(vt.truckId)) || vt.truckNumber || '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Contractor</Text>
                  <Text size="sm">{vt.contractorId ? (contractors.find(c => String(c.id) === vt.contractorId)?.companyName || vt.contractorId) : '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Zone</Text>
                  <Text size="sm">{vt.zoneId ? zoneName(vt.zoneId) : '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Disposal Site</Text>
                  <Text size="sm">{vt.disposalSiteId ? siteName(vt.disposalSiteId) : (vt.disposalSiteName || '-')}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Date</Text>
                  <Text size="sm">{vt.pickupTime ? new Date(vt.pickupTime).toLocaleString() : '-'}</Text>
                </div>
                {vt.loadingMonitorName && (
                  <div>
                    <Text size="xs" c="dimmed">Loading Monitor</Text>
                    <Text size="sm">{vt.loadingMonitorName}{vt.loadingLoadPct ? ` (${vt.loadingLoadPct}%)` : ''}</Text>
                  </div>
                )}
                {vt.monitorName && (
                  <div>
                    <Text size="xs" c="dimmed">Tower Monitor</Text>
                    <Text size="sm">{vt.monitorName}{vt.towerLoadPct ? ` (${vt.towerLoadPct}%)` : ''}</Text>
                  </div>
                )}
                {vt.verifiedAt && (
                  <div>
                    <Text size="xs" c="dimmed">Verified At</Text>
                    <Text size="sm">{new Date(vt.verifiedAt).toLocaleString()}</Text>
                  </div>
                )}
                {vt.handLoaded === 'true' && (
                  <div>
                    <Text size="xs" c="dimmed">FEMA Flag</Text>
                    <Badge size="sm" color="#F86A0B">Hand-Loaded (50% cap)</Badge>
                  </div>
                )}
                {vt.subcontractorName && (
                  <div>
                    <Text size="xs" c="dimmed">Subcontractor</Text>
                    <Text size="sm">{vt.subcontractorName}</Text>
                  </div>
                )}
                {vt.clinCode && (
                  <div>
                    <Text size="xs" c="dimmed">CLIN Code</Text>
                    <Text size="sm">{clinLabel(vt.clinCode)}</Text>
                  </div>
                )}
                {vt.distanceMiles && (
                  <div>
                    <Text size="xs" c="dimmed">Distance</Text>
                    <Text size="sm">{parseFloat(vt.distanceMiles).toFixed(1)} mi</Text>
                  </div>
                )}
                {vt.transactionRate && (
                  <div>
                    <Text size="xs" c="dimmed">Rate</Text>
                    <Text size="sm">${parseFloat(vt.transactionRate).toFixed(2)}</Text>
                  </div>
                )}
                {vt.extendedCost && (
                  <div>
                    <Text size="xs" c="dimmed">Extended Cost</Text>
                    <Text size="sm" fw={600}>${parseFloat(vt.extendedCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                  </div>
                )}
              </SimpleGrid>

              {/* Signatures */}
              {(vt.loadingMonitorSignature || vt.driverPickupSignature || vt.monitorSignature || vt.driverSignature) && (
                <>
                  <Divider label="Signatures" labelPosition="left" />
                  <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
                    {vt.loadingMonitorSignature && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Loading Monitor</Text>
                        <img src={vt.loadingMonitorSignature} alt="Loading monitor sig" style={{ maxWidth: 120, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                      </div>
                    )}
                    {vt.driverPickupSignature && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Driver (Pickup)</Text>
                        <img src={vt.driverPickupSignature} alt="Driver pickup sig" style={{ maxWidth: 120, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                      </div>
                    )}
                    {vt.monitorSignature && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Tower Monitor</Text>
                        <img src={vt.monitorSignature} alt="Tower monitor sig" style={{ maxWidth: 120, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                      </div>
                    )}
                    {vt.driverSignature && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Driver (Disposal)</Text>
                        <img src={vt.driverSignature} alt="Driver disposal sig" style={{ maxWidth: 120, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                      </div>
                    )}
                  </SimpleGrid>
                </>
              )}

              {/* Load photo */}
              {vt.photoLoadUrl && (
                <>
                  <Divider label="Load Photo" labelPosition="left" />
                  <img src={vt.photoLoadUrl} alt="Load photo"
                    onClick={() => { setLightboxSrc(vt.photoLoadUrl); setLightboxLabel('Load Photo'); }}
                    style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: '1px solid #e2e8f0' }} />
                </>
              )}

              {vt.notes && (
                <>
                  <Divider label="Notes" labelPosition="left" />
                  <Text size="sm">{vt.notes}</Text>
                </>
              )}

              {vtStops.length > 0 && (
                <>
                  <Divider label={`Pickup Stops (${vtStops.length})`} labelPosition="left" />
                  <Stack gap={6}>
                    {vtStops.map((s: any, idx: number) => {
                      const sCfg = s.debrisType ? DEBRIS_TYPES[s.debrisType as DebrisType] : null;
                      return (
                        <Paper key={s.id || idx} p="xs" radius="sm" withBorder style={{ background: '#F8F8F8' }}>
                          <Group justify="space-between">
                            <Group gap={6}>
                              <Badge size="xs" variant="light">Stop {idx + 1}</Badge>
                              {s.debrisType && <Badge size="xs" color={DEBRIS_TYPE_COLORS[s.debrisType as DebrisType] ?? '#939598'}>{DEBRIS_TYPE_LABELS[s.debrisType as DebrisType] ?? s.debrisType}</Badge>}
                              {s.unitCount && <Text size="xs" fw={600}>{s.unitCount} {sCfg?.unitLabel || 'CY'}</Text>}
                              {s.weight && <Text size="xs" fw={600}>{s.weight} {s.weightUnit || 'lbs'}</Text>}
                            </Group>
                            {s.lat && s.lng && <Text size="xs" c="dimmed">{parseFloat(s.lat).toFixed(4)}, {parseFloat(s.lng).toFixed(4)}</Text>}
                          </Group>
                          {s.address && <Text size="xs" c="dimmed" mt={2}>{s.address}</Text>}
                          {(s.beforePhotoData || s.afterPhotoData) && (
                            <Group gap="xs" mt={6}>
                              {s.beforePhotoData && (
                                <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => { setLightboxSrc(s.beforePhotoData); setLightboxLabel(`Stop ${idx + 1} — Before`); }}>
                                  <img src={s.beforePhotoData} alt="Before" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                                  <Badge size="xs" variant="filled" color="rgba(0,0,0,0.5)" style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 12 }}>Before</Badge>
                                </div>
                              )}
                              {s.afterPhotoData && (
                                <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => { setLightboxSrc(s.afterPhotoData); setLightboxLabel(`Stop ${idx + 1} — After`); }}>
                                  <img src={s.afterPhotoData} alt="After" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                                  <Badge size="xs" variant="filled" color="rgba(0,0,0,0.5)" style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 12 }}>After</Badge>
                                </div>
                              )}
                            </Group>
                          )}
                          {s.notes && <Text size="xs" mt={2}>{s.notes}</Text>}
                        </Paper>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Stack>
          );
        })()}
      </Modal>

      {/* ── Photo Lightbox Modal ──────────────────────── */}
      <Modal opened={!!lightboxSrc} onClose={() => setLightboxSrc(null)}
        title={lightboxLabel} centered size="xl" zIndex={400}
        styles={{ body: { padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#000', minHeight: 400 }, header: { background: '#000', color: '#fff' }, title: { color: '#fff' }, close: { color: '#fff' } }}>
        {lightboxSrc && (
          <img src={lightboxSrc} alt={lightboxLabel}
            style={{ width: '100%', height: '70vh', objectFit: 'contain', imageRendering: 'auto' }} />
        )}
      </Modal>

      {/* Truck Cert PDF Viewer */}
      <Modal opened={!!certPdfUrl} onClose={() => setCertPdfUrl(null)}
        title={certPdfTitle} centered fullScreen zIndex={400}>
        {certPdfUrl && (
          <iframe src={certPdfUrl} style={{ width: '100%', height: 'calc(100vh - 80px)', border: 'none' }} />
        )}
      </Modal>
    </div>
  );
}
